/**
 * Portal-based info popover shown on hover/focus. Supports rich content or raw HTML,
 * auto-flips above the trigger near the last row, and uses `position: fixed` to escape
 * table overflow wrappers.
 */
import {
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useCallback,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils/helpers'
import { DESCRIPTION_TOOLTIP_SURFACE } from '@/lib/utils/descriptionTooltipStyles'
import {
    getPopoverMaxHeightInDirection,
    getTooltipPositionClass,
} from '@/lib/utils/tooltipPosition'
import {
    POPOVER_Z,
    DEFAULT_HIDE_MS,
    DEFAULT_MAX_WIDTH,
    MAX_HEIGHT_VH_RATIO,
    MAX_HEIGHT_CAP,
    TRIGGER_GAP,
    VIEWPORT_INSET,
} from './InfoPopover.constants'
import type {
    InfoPopoverAlign,
    InfoPopoverProps,
    ComputedPosition,
} from './InfoPopover.types'

export type { InfoPopoverAlign, InfoPopoverProps } from './InfoPopover.types'

function computePosition(
    triggerEl: HTMLElement,
    popoverEl: HTMLElement | null,
    align: InfoPopoverAlign,
    maxWidthPx: number,
    isLastRow: boolean,
    clampPanelHeight: boolean,
): ComputedPosition {
    const r = triggerEl.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const maxW = Math.min(maxWidthPx, vw - 16)
    const globalMaxH = clampPanelHeight
        ? Math.min(Math.round(vh * MAX_HEIGHT_VH_RATIO), MAX_HEIGHT_CAP)
        : Math.min(Math.round(vh * 0.65), vh - VIEWPORT_INSET * 2)
    const openUp =
        getTooltipPositionClass(isLastRow, triggerEl, vh, globalMaxH) === 'top'
    const maxHeight = getPopoverMaxHeightInDirection(
        triggerEl,
        openUp,
        vh,
        globalMaxH,
    )
    const measured = popoverEl?.getBoundingClientRect().width ?? maxW
    const popW = Math.min(Math.max(measured, 1), maxW)

    let left: number
    if (align === 'center') {
        const centerX = r.left + r.width / 2
        left = centerX - popW / 2
    } else if (align === 'end') {
        left = r.right - popW
    } else {
        left = r.left
    }
    left = Math.max(VIEWPORT_INSET, Math.min(left, vw - popW - VIEWPORT_INSET))

    const base: ComputedPosition = {
        position: 'fixed',
        left,
        maxWidth: maxW,
        maxHeight,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        zIndex: POPOVER_Z,
    }
    return openUp
        ? { ...base, bottom: vh - r.top + TRIGGER_GAP }
        : { ...base, top: r.bottom + TRIGGER_GAP }
}

function samePosition(a: ComputedPosition, b: ComputedPosition): boolean {
    return (
        a.left === b.left &&
        a.top === b.top &&
        a.bottom === b.bottom &&
        a.maxWidth === b.maxWidth &&
        a.maxHeight === b.maxHeight
    )
}

export function InfoPopover({
    children,
    content,
    contentHtml,
    align = 'center',
    maxWidth = DEFAULT_MAX_WIDTH,
    isLastRow = false,
    className,
    contentClassName,
    hideDelayMs = DEFAULT_HIDE_MS,
    testId,
    inline = false,
    clampPanelHeight = true,
    closeOnClick = false,
}: InfoPopoverProps) {
    const [open, setOpen] = useState(false)
    const [style, setStyle] = useState<ComputedPosition | null>(null)
    const triggerRef = useRef<HTMLSpanElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearHide = useCallback(() => {
        if (hideTimerRef.current !== null) {
            clearTimeout(hideTimerRef.current)
            hideTimerRef.current = null
        }
    }, [])

    const scheduleHide = useCallback(() => {
        clearHide()
        hideTimerRef.current = setTimeout(() => {
            hideTimerRef.current = null
            setOpen(false)
        }, hideDelayMs)
    }, [clearHide, hideDelayMs])

    const handleEnter = useCallback(() => {
        clearHide()
        setOpen(true)
    }, [clearHide])

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear positioning style when the popover is closed
            setStyle(null)
            return
        }
        const next = computePosition(
            triggerRef.current,
            popoverRef.current,
            align,
            maxWidth,
            isLastRow,
            clampPanelHeight,
        )
        setStyle((prev) => (prev && samePosition(prev, next) ? prev : next))
        // Re-runs after the portal mounts so alignment can use the real popover width.
    }, [open, align, maxWidth, isLastRow, clampPanelHeight, style])

    useEffect(() => {
        if (!open) return
        const sync = () => {
            if (!triggerRef.current) return
            setStyle(
                computePosition(
                    triggerRef.current,
                    popoverRef.current,
                    align,
                    maxWidth,
                    isLastRow,
                    clampPanelHeight,
                ),
            )
        }
        window.addEventListener('scroll', sync, true)
        window.addEventListener('resize', sync)
        return () => {
            window.removeEventListener('scroll', sync, true)
            window.removeEventListener('resize', sync)
        }
    }, [open, align, maxWidth, isLastRow, clampPanelHeight])

    useEffect(() => () => clearHide(), [clearHide])

    const hasHtml =
        contentHtml !== undefined && contentHtml !== null && contentHtml !== ''
    const hasNode = content !== undefined && content !== null && content !== false
    const hasContent = hasHtml || hasNode

    const popover =
        open &&
        hasContent &&
        style !== null &&
        typeof document !== 'undefined' &&
        createPortal(
            <div
                ref={popoverRef}
                role="tooltip"
                data-testid={testId}
                style={style}
                className={cn(
                    DESCRIPTION_TOOLTIP_SURFACE,
                    'p-2 text-xs leading-snug shadow-lg break-words',
                    className,
                )}
                onMouseEnter={clearHide}
                onMouseLeave={scheduleHide}
            >
                {hasHtml ? (
                    <div
                        className={cn('whitespace-pre-wrap', contentClassName)}
                        dangerouslySetInnerHTML={{ __html: contentHtml as string }}
                    />
                ) : (
                    <div className={contentClassName}>{content}</div>
                )}
            </div>,
            document.body,
        )

    return (
        <>
            <span
                ref={triggerRef}
                className={
                    inline
                        ? 'relative inline-block max-w-full min-w-0 align-middle leading-none'
                        : 'relative inline-flex items-center justify-center align-middle leading-none'
                }
                onMouseEnter={handleEnter}
                onMouseLeave={scheduleHide}
                onFocus={handleEnter}
                onBlur={scheduleHide}
                onClick={() => {
                    if (closeOnClick) setOpen(false)
                }}
            >
                {children}
            </span>
            {popover}
        </>
    )
}
